import { useState, useEffect, useMemo, useRef, useLayoutEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/axios';
import { createIssue, getEpicsForProject, getSubtasks, reassignIssue, updateIssueStatus } from '../api/issueApi';
import MentionComposer from './MentionComposer';
import { resolveAssigneeIdsFromText } from '../utils/dailyNotebookMentions';
import { getCustomTeams } from '../api/configApi';
import toast from 'react-hot-toast';
import {
  mergeTeamsForDepartment,
  filterAssignableUsersByTeam,
  normalizeProjectMembersResponse,
} from '../utils/assigneeTeamFilter';
import { filterEpicsForSprintPicker } from '../utils/epicSprintFilter';
import IssueForm from './IssueForm';
import { issueStatusLabel, getWorkflowStatusOptions } from '../constants/issueStatuses';
import { AuthContext } from '../auth/AuthContext';
import { DEFAULT_DEPARTMENT } from '../constants/departments';
import { toDateInputValue } from '../utils/dateOnly';
import { lockAppScroll } from '../utils/lockAppScroll';
import AutoGrowTextarea from './ui/AutoGrowTextarea';

const PRIORITY_OPTIONS = ['Blocker', 'Critical', 'High', 'Medium', 'Low'];

const CLOSED_ISSUE_STATUSES = ['Fixed', 'Not Reproducible', "Invalid", "Won't Fix", 'Duplicate', 'Closed'];

const DESCRIPTION_MIN_VISIBLE_LINES = 3;
const DESCRIPTION_MAX_VISIBLE_LINES_STORY = 18;
const DESCRIPTION_MAX_VISIBLE_LINES_TASK = 8;

/** Grow description field with content; default ~3 lines, scroll after max. */
function syncDescriptionTextareaHeight(el, maxVisibleLines) {
  if (!el) return;
  const cs = window.getComputedStyle(el);
  const lineHeight = parseFloat(cs.lineHeight);
  const fontSize = parseFloat(cs.fontSize) || 14;
  const lh = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : fontSize * 1.55;
  const verticalChrome =
    parseFloat(cs.paddingTop) +
    parseFloat(cs.paddingBottom) +
    parseFloat(cs.borderTopWidth) +
    parseFloat(cs.borderBottomWidth);
  const minH = lh * DESCRIPTION_MIN_VISIBLE_LINES + verticalChrome;
  const maxH = lh * maxVisibleLines + verticalChrome;
  el.style.height = 'auto';
  const contentH = el.scrollHeight;
  const next = Math.min(Math.max(contentH, minH), maxH);
  el.style.height = `${next}px`;
  el.style.overflowY = contentH > maxH ? 'auto' : 'hidden';
}

const taskFirstLine = (issue) =>
  (issue?.description || '').split('\n')[0]?.trim() || `Task #${issue?.issueid}`;

const TASK_STATUS_COLORS = {
  Open: { bg: '#f1f5f9', color: '#475569' },
  'In Progress': { bg: '#EAF6FD', color: '#2C63AE' },
  Testing: { bg: '#fef3c7', color: '#b45309' },
  Blocked: { bg: '#fee2e2', color: '#b91c1c' },
  Closed: { bg: '#dcfce7', color: '#15803d' },
  Done: { bg: '#dcfce7', color: '#15803d' },
};

const fieldControl = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.85rem',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  background: '#f8fafc',
  boxSizing: 'border-box',
};

/** Shared height for action row buttons and selects (avoids misaligned native controls). */
const ACTION_BAR_H = 38;
const actionBarSelectBase = {
  height: ACTION_BAR_H,
  minHeight: ACTION_BAR_H,
  padding: '0 10px',
  fontSize: '0.8rem',
  fontWeight: 500,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  boxSizing: 'border-box',
};
const actionBarBtnBase = {
  minHeight: ACTION_BAR_H,
  height: ACTION_BAR_H,
  padding: '0 14px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  borderRadius: 8,
  fontSize: '0.8rem',
  fontWeight: 600,
};

const resolveUserLabel = (users, raw) => {
  if (raw == null || raw === '') return 'Unassigned';
  const u = (users || []).find((x) => String(x.id) === String(raw));
  return u?.full_name || u?.email || String(raw);
};

/** Human-readable audit line for issue_activities rows (non-comments). */
const formatAuditLine = (activity, users) => {
  if (activity.action_type === 'commented') return null;
  const ov = activity.old_value;
  const nv = activity.new_value;
  const fc = activity.field_changed;
  const at = activity.action_type;

  if (at === 'created') {
    return `Created (${activity.new_value || 'issue'})`;
  }
  if (fc === 'story_points') {
    return `Story points: ${ov ?? '0'} → ${nv ?? '0'}`;
  }
  if (fc === 'priority') {
    return `Priority: ${ov || '—'} → ${nv || '—'}`;
  }
  if (fc === 'assignee') {
    return `Assignee: ${resolveUserLabel(users, ov)} → ${resolveUserLabel(users, nv)}`;
  }
  if (fc === 'status') {
    return `Status: ${ov || '—'} → ${nv || '—'}`;
  }
  if (fc === 'description') {
    return 'Description updated';
  }
  if (fc === 'sprint') {
    return `Sprint: ${ov || '—'} → ${nv || '—'}`;
  }
  if (fc === 'assigneeteam') {
    return `Team: ${ov || '—'} → ${nv || '—'}`;
  }
  if (fc === 'due_date') {
    return `Due date: ${ov || '—'} → ${nv || '—'}`;
  }
  if (at === 'assigned' || at === 'reassigned') {
    const name = activity.assignee_name || activity.assignee_email || resolveUserLabel(users, nv);
    return at === 'reassigned' ? `Reassigned to ${name}` : `Assigned to ${name}`;
  }
  if (at === 'updated' && fc) {
    return `${fc.replace(/_/g, ' ')}: ${ov || '—'} → ${nv || '—'}`;
  }
  if (at === 'closed' || at === 'reopened' || at === 'status_changed') {
    return `Status: ${ov || '—'} → ${nv || '—'}`;
  }
  return at || 'Activity';
};

/**
 * Title and body are stored in one column as `title\n\nbody`. Parsing keeps
 * everything after the first line — including the blank separator — so
 * composing must not add another one, or an untouched save would rewrite the
 * text it just read.
 */
function composeDescription(title, body) {
  const head = String(title || '').trim();
  const rest = String(body || '');
  return rest.trim() ? `${head}\n${rest.replace(/^\n/, '\n')}`.replace(/\s+$/, '') : head;
}

const TaskDetailModal = ({
  task,
  onClose,
  onStatusChange: _onStatusChange,
  onReassign: _onReassign,
  users = [],
  childTaskCount = 0,
  /** Optional seed from board payload while subtasks query loads. */
  childTasks: childTasksSeed = null,
  /** Open a child task in the detail modal (e.g. swap selection on sprint board). */
  onOpenChildTask = null,
  /** Return to parent story after drilling into a child task. */
  onBackToParent = null,
  onMoveToBacklog,
  /**
   * Move this work item to a sprint, or to the backlog with a null target.
   * Supersedes onMoveToBacklog, which is still accepted so callers that only
   * pass the old prop keep working — it is adapted to this shape below.
   */
  onMoveToSprint: onMoveToSprintProp,
  /** Sprints offered as move targets; the caller decides which are eligible. */
  moveTargetSprints = [],
  onMoveToStory,
  availableStories = [],
  /** Row for `task.parent_id` from board payload (to know if parent is Epic vs Story). */
  parentIssue = null,
  canEdit = false,
  /** Admin/manager — delete tasks & stories in detail modal */
  canDelete = false,
  onDeleteIssue = null,
  /** When viewing a Story — pre-fill Add Task form (project, sprint, story, epic). */
  projectId = null,
  sprintId = null,
  onTaskCreated = null,
  navigationIds = null,
  onNavigate = null,
}) => {
  const qc = useQueryClient();
  /**
   * Description is open to whoever can open this modal at all — same as the
   * title field just above it, which has never been gated. `canEdit` stays
   * the narrower steward-only flag for reassignment, status changes on
   * child tasks, and moving items to backlog.
   */
  const canEditDescription = true;
  const { user } = useContext(AuthContext);

  /**
   * Work that came from Tracker. Tracker owns its stage's schedule and
   * overwrites the story's due date whenever the stage ETA moves, so editing
   * the date here would be silently undone — the field is shown read-only and
   * the requirement is one click away instead.
   */
  const trackerRequirementId = task.tracker_requirement_id || null;
  const isTrackerStory = !!trackerRequirementId;
  const trackerHref = isTrackerStory
    ? `/tracker?project=${encodeURIComponent(task.tracker_project_id || '')}` +
      `&requirement=${trackerRequirementId}` +
      `&state=${encodeURIComponent(task.tracker_state || 'active')}`
    : null;

  const boardDepartment = user?.department || DEFAULT_DEPARTMENT;
  const workflowOptions = useMemo(
    () => getWorkflowStatusOptions(boardDepartment),
    [boardDepartment]
  );
  const [comment, setComment] = useState('');
  const [showActivities, setShowActivities] = useState(true);
  // Open by default, whether or not it already has text — description is
  // now always editable (see canEditDescription), so an empty panel is an
  // invitation to add one rather than clutter to hide.
  const [showDescription, setShowDescription] = useState(true);
  const [draftStatus, setDraftStatus] = useState(task.status);
  const [draftAssignee, setDraftAssignee] = useState(String(task.assignee_id || ''));
  const [draftPriority, setDraftPriority] = useState(task.priority || 'High');
  const [draftStoryPoints, setDraftStoryPoints] = useState(() => String(Number(task.story_points) || 0));
  const [draftTitle, setDraftTitle] = useState(() => task.description?.split('\n')[0] || '');
  const [draftDueDate, setDraftDueDate] = useState(() => toDateInputValue(task.due_date));
  const [draftTeam, setDraftTeam] = useState(() => task.assigneeteam || '');
  const [draftDescBody, setDraftDescBody] = useState(() => {
    const lines = (task.description || '').split('\n');
    return lines.slice(1).join('\n');
  });
  const [draftType, setDraftType] = useState(task.issuetype);
  const [converting, setConverting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState('');
  const [draftEpicParent, setDraftEpicParent] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [movingSprint, setMovingSprint] = useState(false);
  /**
   * One handler for both props: callers that pass onMoveToSprint get it
   * directly, older ones that only pass onMoveToBacklog still work for the
   * backlog case (their handler takes just an id and always means "backlog").
   */
  const onMoveToSprint = onMoveToSprintProp
    || (onMoveToBacklog ? (issueId, sprintId) => (sprintId == null ? onMoveToBacklog(issueId) : undefined) : null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [quickTaskText, setQuickTaskText] = useState('');
  const [addingQuickTask, setAddingQuickTask] = useState(false);
  const [togglingTaskIds, setTogglingTaskIds] = useState([]);
  const [reassigningTaskIds, setReassigningTaskIds] = useState([]);
  const [markingAllDone, setMarkingAllDone] = useState(false);
  const descriptionRef = useRef(null);

  const navIndex = useMemo(() => {
    if (!Array.isArray(navigationIds) || navigationIds.length === 0) return -1;
    return navigationIds.findIndex((id) => String(id) === String(task.issueid));
  }, [navigationIds, task.issueid]);
  const prevIssueId = navIndex > 0 ? navigationIds[navIndex - 1] : null;
  const nextIssueId =
    navIndex >= 0 && navIndex < navigationIds.length - 1 ? navigationIds[navIndex + 1] : null;
  const showNav = typeof onNavigate === 'function' && navIndex >= 0 && navigationIds.length > 1;

  const isStory = draftType === 'Story' || task.issuetype === 'Story';
  const descriptionMaxLines = isStory
    ? DESCRIPTION_MAX_VISIBLE_LINES_STORY
    : DESCRIPTION_MAX_VISIBLE_LINES_TASK;
  const resolvedProjectId = projectId || task.projectid || task.project_id || null;
  const resolvedSprintId = sprintId ?? task.sprint_id ?? null;
  const taskParentStory = useMemo(() => {
    if (!isStory) return null;
    const storyLabel =
      (task.description || '').split('\n')[0]?.trim().slice(0, 100) || `Story #${task.issueid}`;
    const epicParent = parentIssue?.issuetype === 'Epic' ? parentIssue : null;
    return {
      storyId: task.issueid,
      storyLabel,
      epic: epicParent
        ? {
            id: epicParent.issueid,
            label:
              (epicParent.description || '').split('\n')[0]?.trim().slice(0, 100)
              || `Epic #${epicParent.issueid}`,
            epic_status: epicParent.epic_status,
          }
        : null,
    };
  }, [isStory, task.issueid, task.description, parentIssue]);

  const { data: storyChildTasks = [], isLoading: loadingStoryTasks } = useQuery({
    queryKey: ['story-subtasks', task.issueid],
    queryFn: () => getSubtasks(task.issueid).then((r) => r.data),
    enabled: isStory && !!task.issueid,
    initialData: Array.isArray(childTasksSeed) ? childTasksSeed : undefined,
  });

  const storyTaskCount = storyChildTasks.length;
  const storyOpenTasks = useMemo(
    () => storyChildTasks.filter((t) => !CLOSED_ISSUE_STATUSES.includes(t.status)),
    [storyChildTasks]
  );
  const storyDoneCount = storyTaskCount - storyOpenTasks.length;

  const parentStoryIdForSubtasks = useMemo(() => {
    if (parentIssue?.issuetype === 'Story') return parentIssue.issueid;
    return null;
  }, [parentIssue?.issuetype, parentIssue?.issueid]);

  const refreshParentStoryTaskList = async (childIssueId, patch = {}) => {
    if (!parentStoryIdForSubtasks) return;
    const storyKey = ['story-subtasks', parentStoryIdForSubtasks];
    const legacyKey = ['selected-story-subtasks', parentStoryIdForSubtasks];

    if (childIssueId && Object.keys(patch).length > 0) {
      const patchEntry = (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((t) =>
          String(t.issueid) === String(childIssueId) ? { ...t, ...patch } : t,
        );
      };
      qc.setQueryData(storyKey, patchEntry);
      qc.setQueryData(legacyKey, patchEntry);
    }

    await Promise.all([
      qc.invalidateQueries({ queryKey: storyKey }),
      qc.invalidateQueries({ queryKey: legacyKey }),
      qc.invalidateQueries({ queryKey: ['my-issues'] }),
    ]);
  };

  const isTaskUnderStory =
    (draftType === 'Task' || task.issuetype === 'Task') && parentIssue?.issuetype === 'Story';
  const showConvertTypeButton =
    !isTaskUnderStory &&
    (draftType === 'Task' || (draftType === 'Story' && storyTaskCount === 0));
  const storyOrTaskLinksEpic = task.issuetype === 'Task' || task.issuetype === 'Story';

  const { data: activitiesData = [], isLoading: isLoadingActivities } = useQuery({
    queryKey: ['task-activities', task.issueid],
    queryFn: () => api.get(`/issues/${task.issueid}/activities`).then(r => r.data),
    enabled: !!task.issueid,
  });

  const { data: epicsData = [], isPending: epicsLoading } = useQuery({
    queryKey: ['project-epics-task-modal', task.projectid],
    queryFn: () => getEpicsForProject(task.projectid).then((r) => r.data),
    enabled: !!task.projectid && !!canEdit && storyOrTaskLinksEpic,
  });
  const epicsList = useMemo(
    () => filterEpicsForSprintPicker(epicsData, task.sprint_id),
    [epicsData, task.sprint_id],
  );

  const { data: projectMembersRaw } = useQuery({
    queryKey: ['project-members', task.projectid],
    queryFn: () => api.get(`/projects/${task.projectid}/members`).then((r) => r.data),
    enabled: !!task.projectid,
  });
  const { data: customTeamsTask = [] } = useQuery({
    queryKey: ['custom-teams'],
    queryFn: getCustomTeams,
  });
  const taskModalTeams = useMemo(
    () => mergeTeamsForDepartment(customTeamsTask, boardDepartment),
    [customTeamsTask, boardDepartment],
  );
  const taskProjectMembers = normalizeProjectMembersResponse(projectMembersRaw);
  const taskBaseAssignable = useMemo(() => {
    const base = taskProjectMembers.length > 0
      ? taskProjectMembers.filter((u) => !['admin', 'superadmin'].includes(u.role?.toLowerCase()))
      : users.filter((u) => !['admin', 'superadmin'].includes(u.role?.toLowerCase()));
    return base;
  }, [taskProjectMembers, users]);
  const taskFilteredAssignable = useMemo(
    () => filterAssignableUsersByTeam(taskBaseAssignable, draftTeam, taskModalTeams),
    [taskBaseAssignable, draftTeam, taskModalTeams],
  );

  const savedEpicParentId =
    storyOrTaskLinksEpic && parentIssue?.issuetype === 'Epic' ? String(task.parent_id) : '';

  const titleRef = useRef(null);

  // Grow the title box to fit its content so nothing is cut off mid-sentence.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draftTitle]);

  useEffect(() => {
    setDraftStatus(task.status);
    setDraftAssignee(String(task.assignee_id || ''));
    setDraftPriority(task.priority || 'High');
    setDraftStoryPoints(String(Number(task.story_points) || 0));
    setDraftTitle(task.description?.split('\n')[0] || '');
    setDraftDueDate(toDateInputValue(task.due_date));
    setDraftTeam(task.assigneeteam || '');
    {
      const lines = (task.description || '').split('\n');
      setDraftDescBody(lines.slice(1).join('\n'));
    }
    setDraftType(task.issuetype);
    if (storyOrTaskLinksEpic && parentIssue?.issuetype === 'Epic') {
      setDraftEpicParent(String(task.parent_id));
    } else {
      setDraftEpicParent('');
    }
    setSelectedStoryId('');
  }, [
    task.issueid,
    task.status,
    task.assignee_id,
    task.priority,
    task.story_points,
    task.due_date,
    task.assigneeteam,
    task.description,
    task.issuetype,
    task.parent_id,
    parentIssue?.issueid,
    parentIssue?.issuetype,
  ]);

  useEffect(() => {
    if (!draftAssignee) return;
    const ok = taskFilteredAssignable.some((u) => String(u.id) === String(draftAssignee));
    if (!ok) setDraftAssignee('');
  }, [task.issueid, taskFilteredAssignable, draftAssignee]);

  const normalizedDraftPoints = useMemo(() => {
    const p = parseInt(String(draftStoryPoints).trim(), 10);
    return Number.isNaN(p) ? 0 : Math.max(0, Math.min(100, p));
  }, [draftStoryPoints]);

  const savedDueDate = toDateInputValue(task.due_date);
  const showStoryPicker =
    Boolean(
      canEdit
      && onMoveToStory
      && task.issuetype === 'Task'
      && availableStories.length > 0
      && (!task.parent_id || parentIssue?.issuetype === 'Epic'),
    );
  const showEpicPicker = Boolean(storyOrTaskLinksEpic && canEdit && task.projectid);
  const isDirty = useMemo(() => {
    const savedPts = Number(task.story_points) || 0;
    const savedAssignee = String(task.assignee_id || '');
    const savedDue = toDateInputValue(task.due_date);
    const savedTeamVal = task.assigneeteam || '';
    // Rejoin with the blank line the description was stored with, so opening
    // an issue and saving without touching the text leaves it byte-identical.
    // (Parsing keeps everything after the first line, blank line included.)
    const composed = composeDescription(draftTitle, draftDescBody);
    const epicDirty =
      storyOrTaskLinksEpic &&
      String(draftEpicParent || '') !== String(savedEpicParentId || '');
    const descDirty = composed !== (task.description || '').trim();
    const storyPickerDirty = showStoryPicker && String(selectedStoryId || '').trim() !== '';
    return (
      draftStatus !== task.status
      || draftAssignee !== savedAssignee
      || draftPriority !== (task.priority || 'High')
      || normalizedDraftPoints !== savedPts
      || (!isTrackerStory && draftDueDate !== savedDue)
      || (draftTeam || '') !== savedTeamVal
      || epicDirty
      || descDirty
      || storyPickerDirty
    );
  }, [
    draftStatus,
    draftAssignee,
    draftPriority,
    normalizedDraftPoints,
    draftTitle,
    draftDescBody,
    draftDueDate,
    isTrackerStory,
    draftTeam,
    draftEpicParent,
    savedEpicParentId,
    storyOrTaskLinksEpic,
    selectedStoryId,
    showStoryPicker,
    task.status,
    task.assignee_id,
    task.priority,
    task.story_points,
    task.due_date,
    task.description,
    task.issuetype,
    task.assigneeteam,
  ]);

  const addCommentMutation = useMutation({
    mutationFn: (commentText) => api.post(`/issues/${task.issueid}/comment`, { comment: commentText }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-activities', task.issueid] });
      setComment('');
      toast.success('Comment added');
    },
    onError: () => toast.error('Failed to add comment'),
  });

  const handleAddComment = () => {
    if (!comment.trim()) return;
    addCommentMutation.mutate(comment.trim());
  };

  const handleSaveDetails = async () => {
    if (!isDirty) {
      toast('No changes to save');
      return;
    }
    const payload = {};
    if (draftStatus !== task.status) payload.status = draftStatus;
    if (draftAssignee !== String(task.assignee_id || '')) {
      payload.assignee_id = draftAssignee === '' ? null : Number(draftAssignee);
    }
    if (draftPriority !== (task.priority || 'High')) payload.priority = draftPriority;
    if (normalizedDraftPoints !== (Number(task.story_points) || 0)) {
      payload.story_points = normalizedDraftPoints;
    }
    // Same helper the dirty check uses, so what we compare is what we send.
    const composedDesc = composeDescription(draftTitle, draftDescBody);
    if (composedDesc !== (task.description || '').trim()) {
      payload.description = composedDesc;
    }
    if ((draftTeam || '') !== (task.assigneeteam || '')) {
      payload.assigneeteam = draftTeam || null;
    }
    // Tracker owns the schedule for its own stories — never send a date it
    // would only overwrite on the next stage change.
    if (!isTrackerStory && draftDueDate !== savedDueDate) {
      payload.due_date = draftDueDate || null;
    }
    const storyPick = String(selectedStoryId || '').trim();
    if (showStoryPicker && storyPick) {
      payload.parent_id = Number(storyPick);
    } else if (
      storyOrTaskLinksEpic &&
      String(draftEpicParent || '') !== String(savedEpicParentId || '')
    ) {
      payload.parent_id = draftEpicParent ? Number(draftEpicParent) : null;
    }
    if (Object.keys(payload).length === 0) {
      toast('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/issues/${task.issueid}`, payload);
      const savedIssue = data?.issue || data;
      const listPatch = savedIssue && typeof savedIssue === 'object'
        ? { ...payload, ...savedIssue }
        : payload;
      await qc.invalidateQueries({ queryKey: ['board'] });
      await qc.invalidateQueries({ queryKey: ['issue', task.issueid] });
      await qc.invalidateQueries({ queryKey: ['task-activities', task.issueid] });
      const markingStoryDone =
        isStory
        && CLOSED_ISSUE_STATUSES.includes(draftStatus)
        && !CLOSED_ISSUE_STATUSES.includes(task.status);
      if (markingStoryDone) {
        qc.setQueryData(['story-subtasks', task.issueid], (old) => (
          Array.isArray(old) ? old.map((t) => ({ ...t, status: 'Closed' })) : old
        ));
      } else if (isStory && draftStatus !== task.status) {
        await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      }
      await refreshParentStoryTaskList(task.issueid, listPatch);
      setSelectedStoryId('');
      onTaskCreated?.(savedIssue && typeof savedIssue === 'object' ? savedIssue : listPatch);
      toast.success('Changes saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  };

  const handleChildTaskStatusChange = async (issueId, status) => {
    try {
      const resolution = status === 'Fixed' ? 'Fixed' : undefined;
      await updateIssueStatus(issueId, status, resolution);
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      onTaskCreated?.();
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  /** Inline assignee change on a child task row. */
  const handleChildTaskReassign = async (issueId, assigneeIdRaw) => {
    const nextId = assigneeIdRaw === '' ? null : Number(assigneeIdRaw);
    setReassigningTaskIds((prev) => [...prev, issueId]);
    qc.setQueryData(['story-subtasks', task.issueid], (old) =>
      Array.isArray(old)
        ? old.map((t) => (t.issueid === issueId ? { ...t, assignee_id: nextId } : t))
        : old
    );
    try {
      // reassignIssue rejects a null assignee, so clearing goes via updateIssue.
      if (nextId === null) {
        await api.put(`/issues/${issueId}`, { assignee_id: null });
      } else {
        await reassignIssue(issueId, nextId);
      }
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      onTaskCreated?.();
      toast.success('Assignee updated');
    } catch (err) {
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      toast.error(err.response?.data?.message || 'Failed to change assignee');
    } finally {
      setReassigningTaskIds((prev) => prev.filter((id) => id !== issueId));
    }
  };

  /** Checkbox tick/untick — closes or reopens one child task. */
  const handleToggleChildTaskDone = async (child) => {
    if (!canEdit) return;
    const done = CLOSED_ISSUE_STATUSES.includes(child.status);
    const nextStatus = done ? 'Reopened' : 'Closed';
    setTogglingTaskIds((prev) => [...prev, child.issueid]);
    // Flip it locally first so the tick feels instant; the refetch confirms.
    qc.setQueryData(['story-subtasks', task.issueid], (old) =>
      Array.isArray(old)
        ? old.map((t) => (t.issueid === child.issueid ? { ...t, status: nextStatus } : t))
        : old
    );
    try {
      await updateIssueStatus(child.issueid, nextStatus);
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      onTaskCreated?.();
    } catch (err) {
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      toast.error(err.response?.data?.message || 'Failed to update task');
    } finally {
      setTogglingTaskIds((prev) => prev.filter((id) => id !== child.issueid));
    }
  };

  /** Close every still-open task under this story. */
  const handleMarkAllTasksDone = async () => {
    if (!canEdit || storyOpenTasks.length === 0) return;
    const n = storyOpenTasks.length;
    if (!window.confirm(`Mark all ${n} open task${n > 1 ? 's' : ''} in this story as done?`)) {
      return;
    }
    setMarkingAllDone(true);
    try {
      const results = await Promise.allSettled(
        storyOpenTasks.map((t) => updateIssueStatus(t.issueid, 'Closed'))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      onTaskCreated?.();
      if (failed > 0) {
        toast.error(`${n - failed} marked done, ${failed} failed`);
      } else {
        toast.success(`All ${n} task${n > 1 ? 's' : ''} marked done`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark tasks done');
    } finally {
      setMarkingAllDone(false);
    }
  };

  /** Notebook-style quick add: "Task title @person" → one child Task. */
  const handleQuickAddTask = async () => {
    const raw = quickTaskText.trim();
    if (!raw || addingQuickTask) return;
    if (!resolvedProjectId) {
      toast.error('No project on this story');
      return;
    }

    // @ is a convenience, not a requirement — same as the "+ Add Task" form,
    // which leaves its own Assignee dropdown on Unassigned by default.
    const assigneeIds = resolveAssigneeIdsFromText(raw, taskProjectMembers);
    const assigneeId = assigneeIds[0] ?? null;
    const member = taskProjectMembers.find((m) => Number(m.id) === Number(assigneeId));

    setAddingQuickTask(true);
    try {
      await createIssue({
        projectid: resolvedProjectId,
        sprint_id: resolvedSprintId,
        issuetype: 'Task',
        description: raw,
        assignee_id: assigneeId,
        assigneeteam: member?.team || draftTeam || 'Any',
        priority: draftPriority || 'High',
        due_date: draftDueDate || undefined,
        parent_id: task.issueid,
      });
      setQuickTaskText('');
      await qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
      await qc.invalidateQueries({ queryKey: ['board'] });
      onTaskCreated?.();
      toast.success(member ? `Task added for ${member.full_name}` : 'Task added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add task');
    } finally {
      setAddingQuickTask(false);
    }
  };

  const stopChildCardNav = (e) => e.stopPropagation();

  // Lock background scroll without breaking Layout overflow after close
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const unlockMain = lockAppScroll();
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      unlockMain();
    };
  }, []);

  useLayoutEffect(() => {
    syncDescriptionTextareaHeight(descriptionRef.current, descriptionMaxLines);
  }, [draftDescBody, descriptionMaxLines, task.issueid, draftType]);

  const typeLabel = draftType === 'Story' ? '📖 Story' : '📋 Task';

  const modalTree = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 1200,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          padding: '1.5rem',
          border: '2px solid transparent',
          borderColor: isDirty ? '#f97316' : 'transparent',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s ease',
        }}
      >
        {/* Header — match Story detail modal */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.75rem',
            flexWrap: 'wrap',
            rowGap: 8,
            columnGap: 12,
          }}
        >
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            {typeof onBackToParent === 'function' && (
              <button
                type="button"
                onClick={onBackToParent}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 8,
                  padding: '4px 10px',
                  border: '1px solid #c7d2fe',
                  borderRadius: 8,
                  background: '#eef2ff',
                  color: '#4338ca',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ← Back to Story
              </button>
            )}
            <div style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '0.25rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              {draftType} #{task.issueid}
            </div>
            {/* Textarea rather than input so a long title wraps instead of
                scrolling out of view; it grows to fit and Enter still saves. */}
            <textarea
              ref={titleRef}
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              rows={1}
              style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#0f172a',
                border: 'none',
                borderBottom: '2px solid transparent',
                outline: 'none',
                width: '100%',
                background: 'transparent',
                padding: '2px 0',
                borderRadius: 0,
                resize: 'none',
                overflow: 'hidden',
                lineHeight: 1.3,
                fontFamily: 'inherit',
                display: 'block',
              }}
              onFocus={(e) => { e.target.style.borderBottomColor = '#2C8FC7'; }}
              onBlur={(e) => { e.target.style.borderBottomColor = 'transparent'; }}
              placeholder={draftType === 'Story' ? 'Story title' : 'Task title'}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            {showNav && (
              <>
                <button
                  type="button"
                  disabled={!prevIssueId}
                  onClick={() => prevIssueId && onNavigate(prevIssueId)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    background: prevIssueId ? 'white' : '#f9fafb',
                    color: prevIssueId ? '#374151' : '#d1d5db',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: prevIssueId ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  disabled={!nextIssueId}
                  onClick={() => nextIssueId && onNavigate(nextIssueId)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    background: nextIssueId ? 'white' : '#f9fafb',
                    color: nextIssueId ? '#374151' : '#d1d5db',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: nextIssueId ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Next →
                </button>
                <span style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {navIndex + 1} / {navigationIds.length}
                </span>
              </>
            )}
            {isStory && resolvedProjectId && (
              <button
                type="button"
                onClick={() => setShowAddTask(true)}
                title="Add a task under this story"
                style={{
                  ...actionBarBtnBase,
                  padding: '0 14px',
                  border: 'none',
                  background: '#1272B3',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                + Add Task
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#94a3b8',
                padding: 0,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          marginBottom: '0.75rem',
          padding: '4px 0',
          borderBottom: '1px solid #f1f5f9',
        }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#EAF6FD', color: '#1272B3' }}>
            {typeLabel}
          </span>
          {task.projectname && (
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>📁 {task.projectname}</span>
          )}
          {task.sprint_name && (
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>🏃 {task.sprint_name}</span>
          )}
          {/* Where this work came from, and the way back to it. The stage owns
              the schedule, so people need to know they're looking at a mirror. */}
          {isTrackerStory && (
            <a
              href={trackerHref}
              style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 6,
                background: '#ede9fe',
                color: '#5b21b6',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
              title={`Open "${task.tracker_title || 'this requirement'}" in Tracker`}
            >
              📡 Tracker{task.tracker_stage_code ? ` · ${task.tracker_stage_code}` : ''} ↗
            </a>
          )}
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#475569', marginBottom: '0.75rem' }}>
            Details
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '1rem',
            }}
          >
            <div>
              <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
              <select value={draftPriority} onChange={(e) => setDraftPriority(e.target.value)} style={fieldControl}>
                {PRIORITY_OPTIONS.map((priority) => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Status</label>
              {CLOSED_ISSUE_STATUSES.includes(draftStatus) ? (
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#16a34a', padding: '6px 0' }}>
                  ✓ {issueStatusLabel(draftStatus, boardDepartment)}
                  <button
                    type="button"
                    onClick={() => setDraftStatus('Reopened')}
                    style={{
                      marginLeft: 8,
                      background: 'none',
                      border: 'none',
                      color: '#d97706',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      fontWeight: 700,
                      padding: 0,
                    }}
                  >
                    ↩ Reopen
                  </button>
                </div>
              ) : (
                <select value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)} style={fieldControl}>
                  {workflowOptions.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                  <option value="Reopened">Reopened</option>
                  <option disabled style={{ color: '#d1d5db' }}>────────</option>
                  <option value="Closed">{issueStatusLabel('Done', boardDepartment)}</option>
                </select>
              )}
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Story points</label>
              <input
                type="number"
                min={0}
                max={100}
                value={draftStoryPoints}
                onChange={(e) => setDraftStoryPoints(e.target.value)}
                style={fieldControl}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>ETA (Due Date)</label>
              <input
                type="date"
                value={draftDueDate}
                onChange={(e) => setDraftDueDate(e.target.value)}
                disabled={isTrackerStory}
                title={isTrackerStory ? 'Set by the Tracker stage — change it there' : undefined}
                style={
                  isTrackerStory
                    ? { ...fieldControl, background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }
                    : fieldControl
                }
              />
              {isTrackerStory ? (
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>
                  Set by the Tracker stage
                </div>
              ) : null}
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Team</label>
              <select
                value={draftTeam}
                onChange={(e) => {
                  setDraftTeam(e.target.value);
                  setDraftAssignee('');
                }}
                style={fieldControl}
              >
                <option value="">Select team…</option>
                {taskModalTeams.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Assignee</label>
              <select value={draftAssignee} onChange={(e) => setDraftAssignee(e.target.value)} style={fieldControl}>
                <option value="">
                  {taskFilteredAssignable.length === 0 ? 'No people in this team' : 'Unassigned'}
                </option>
                {taskFilteredAssignable.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                ))}
              </select>
              {draftTeam && draftTeam !== 'Any' && taskFilteredAssignable.length === 0 && (
                <span style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 600, display: 'block', marginTop: 4 }}>
                  No project members on {draftTeam} yet.
                </span>
              )}
            </div>
            {storyOrTaskLinksEpic && !canEdit && parentIssue?.issuetype === 'Epic' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Epic</label>
                <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 600 }}>
                  #{task.parent_id}{' '}
                  {(parentIssue.description || '').split('\n')[0]?.slice(0, 72) || 'Epic'}
                </div>
              </div>
            )}
          </div>
            <div
              style={{
                marginTop: '1rem',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 10,
                minWidth: 0,
                rowGap: 10,
              }}
            >
              <button
                type="button"
                onClick={handleSaveDetails}
                disabled={!isDirty || saving}
                style={{
                  ...actionBarBtnBase,
                  flexShrink: 0,
                  padding: '0 18px',
                  border: 'none',
                  background: !isDirty || saving ? '#cbd5e1' : '#1272B3',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>

              {/*
                Move this work to another sprint, or out to the backlog, in one
                control. Backlog was already here as its own button; the sprints
                sit in the same list because "where does this work live" is one
                decision, not two. Tasks are excluded on purpose — the backend
                moves a Task with its parent Story, so offering it here would
                promise something it will not do.
              */}
              {canEdit && onMoveToSprint && (task.issuetype === 'Story' || draftType === 'Story') && (
                <select
                  value=""
                  aria-label="Move to sprint"
                  title="Move this story to another sprint or to the backlog"
                  disabled={movingSprint}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!raw) return;
                    const target = raw === '__backlog__' ? null : Number(raw);
                    const label = raw === '__backlog__'
                      ? 'the backlog'
                      : (moveTargetSprints.find((s) => String(s.id) === raw)?.sprint_name || 'that sprint');
                    if (!window.confirm(`Move this story to ${label}?\n\nTasks under it move with the story.`)) {
                      e.target.value = '';
                      return;
                    }
                    setMovingSprint(true);
                    Promise.resolve(onMoveToSprint(task.issueid, target))
                      .then(() => onClose())
                      .finally(() => setMovingSprint(false));
                  }}
                  style={{
                    ...actionBarSelectBase,
                    width: 190,
                    flex: '0 0 190px',
                    border: '1px dashed #f59e0b',
                    background: '#fffbeb',
                    color: '#d97706',
                    fontWeight: 700,
                    cursor: movingSprint ? 'wait' : 'pointer',
                    opacity: movingSprint ? 0.6 : 1,
                  }}
                >
                  <option value="">{movingSprint ? 'Moving…' : '📦 Move to…'}</option>
                  {moveTargetSprints.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.sprint_name} ({s.status})
                    </option>
                  ))}
                  <option value="__backlog__">Backlog (no sprint)</option>
                </select>
              )}

              {canDelete && onDeleteIssue && (
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => {
                    const label = draftType === 'Story' ? 'story' : 'task';
                    if (!window.confirm(`Permanently delete this ${label} #${task.issueid}? This cannot be undone.`)) return;
                    setDeleting(true);
                    Promise.resolve(onDeleteIssue(task.issueid))
                      .then(() => {
                        toast.success('Deleted');
                        onClose();
                      })
                      .catch((err) => {
                        toast.error(err.response?.data?.message || 'Delete failed');
                      })
                      .finally(() => setDeleting(false));
                  }}
                  style={{
                    ...actionBarBtnBase,
                    flexShrink: 0,
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#dc2626',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    opacity: deleting ? 0.7 : 1,
                  }}
                >
                  {deleting ? '…' : '🗑 Delete'}
                </button>
              )}

              {showEpicPicker ? (
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: 140,
                    minHeight: 0,
                    maxWidth: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <select
                    value={draftEpicParent}
                    onChange={(e) => setDraftEpicParent(e.target.value)}
                    disabled={epicsLoading}
                    aria-label="Epic (optional)"
                    title={
                      task.issuetype === 'Story'
                        ? 'Optional: link this story to a project epic'
                        : parentIssue?.issuetype === 'Story'
                          ? `This task is under story #${parentIssue.issueid}. Choosing an epic moves it under that epic instead.`
                          : 'Optional: link this task to a project epic'
                    }
                    style={{
                      ...actionBarSelectBase,
                      width: '100%',
                      maxWidth: '100%',
                    }}
                  >
                    <option value="">
                      {epicsLoading ? 'Loading epics…' : epicsList.length === 0 ? 'No epics in project' : 'Select Epic'}
                    </option>
                    {epicsList.map((ep) => {
                      const t = (ep.description || '').split('\n')[0].trim() || 'Epic';
                      const short = t.length > 48 ? `${t.slice(0, 48)}…` : t;
                      return (
                        <option key={ep.issueid} value={ep.issueid}>
                          #{ep.issueid} · {short}
                          {ep.epic_status ? ` (${ep.epic_status})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : (
                <div style={{ flex: '1 1 24px', minWidth: 8 }} aria-hidden />
              )}

              {showStoryPicker && (
                <select
                  value={selectedStoryId}
                  onChange={(e) => setSelectedStoryId(e.target.value)}
                  aria-label="Parent story"
                  title="Choose a story, then Save changes to attach this task"
                  style={{
                    ...actionBarSelectBase,
                    width: 280,
                    flex: '0 0 280px',
                    maxWidth: '100%',
                  }}
                >
                  <option value="">Select story…</option>
                  {availableStories.map((story) => (
                    <option key={story.issueid} value={story.issueid}>
                      #{story.issueid} {story.description?.split('\n')[0]?.slice(0, 30) || 'Story'}
                    </option>
                  ))}
                </select>
              )}

              {showConvertTypeButton && (
                <button
                  type="button"
                  disabled={converting}
                  title={draftType === 'Story' && storyTaskCount > 0 ? 'Remove all child tasks before converting' : ''}
                  onClick={async () => {
                    const newType = draftType === 'Task' ? 'Story' : 'Task';
                    if (!window.confirm(`Convert this ${draftType} to a ${newType}?`)) return;
                    setConverting(true);
                    try {
                      await api.put(`/issues/${task.issueid}`, { issuetype: newType });
                      setDraftType(newType);
                      await qc.invalidateQueries({ queryKey: ['board'] });
                      toast.success(`Converted to ${newType}`);
                    } catch (err) {
                      toast.error(err.response?.data?.message || 'Conversion failed');
                    }
                    setConverting(false);
                  }}
                  style={{
                    ...actionBarBtnBase,
                    flexShrink: 0,
                    border: '1px solid #d1d5db',
                    background: '#f9fafb',
                    color: '#374151',
                    cursor: converting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {converting ? 'Converting…' : `↔ Convert to ${draftType === 'Task' ? 'Story' : 'Task'}`}
                </button>
              )}

              {draftType === 'Story' && storyTaskCount > 0 && (
                <span style={{ flexShrink: 0, fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                  Story has {storyTaskCount} task{storyTaskCount !== 1 ? 's' : ''} — remove them to convert to Task
                </span>
              )}
            </div>
            {isDirty && (
              <div
                style={{
                  marginTop: '0.35rem',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: '#c2410c',
                }}
                aria-live="polite"
              >
                Unsaved changes
              </div>
            )}
        </div>

        {/* A Story is just a container for its tasks — the notebook-style
            composer below replaces the free-text description here. Tasks keep
            their description field. */}
        {!isStory && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
              Description
            </label>
            <textarea
              ref={descriptionRef}
              value={draftDescBody}
              onChange={(e) => {
                setDraftDescBody(e.target.value);
                syncDescriptionTextareaHeight(e.target, descriptionMaxLines);
              }}
              onInput={(e) => syncDescriptionTextareaHeight(e.currentTarget, descriptionMaxLines)}
              rows={DESCRIPTION_MIN_VISIBLE_LINES}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '0.9rem',
                lineHeight: 1.55,
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                background: '#f8fafc',
                resize: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                overflow: 'hidden',
              }}
              placeholder="Additional lines after the title (acceptance criteria, notes…)"
            />
          </div>
        )}

        {isStory && (
          <div style={{ marginBottom: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', margin: 0 }}>
                Tasks ({storyDoneCount}/{storyTaskCount} done)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {canEdit && storyTaskCount > 0 && storyOpenTasks.length > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllTasksDone}
                    disabled={markingAllDone}
                    style={{
                      ...actionBarBtnBase,
                      padding: '0 10px',
                      fontSize: '0.75rem',
                      border: '1px solid #bbf7d0',
                      background: '#f0fdf4',
                      color: '#15803d',
                      fontWeight: 700,
                      cursor: markingAllDone ? 'wait' : 'pointer',
                      opacity: markingAllDone ? 0.6 : 1,
                    }}
                  >
                    {markingAllDone
                      ? 'Marking…'
                      : `✓ Mark all done (${storyOpenTasks.length})`}
                  </button>
                )}
                {resolvedProjectId && (
                  <button
                    type="button"
                    onClick={() => setShowAddTask(true)}
                    style={{
                      ...actionBarBtnBase,
                      padding: '0 10px',
                      fontSize: '0.75rem',
                      border: 'none',
                      background: '#EAF6FD',
                      color: '#2C63AE',
                      cursor: 'pointer',
                    }}
                  >
                    + Add Task
                  </button>
                )}
              </div>
            </div>
            {/* Notebook-style quick add: "Task name @person" + Enter, or the
                button — @ is optional, so Enter alone must not be the only
                way to tell this did something. */}
            {canEdit && resolvedProjectId && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <MentionComposer
                      value={quickTaskText}
                      onChange={setQuickTaskText}
                      onSubmit={handleQuickAddTask}
                      members={taskProjectMembers}
                      disabled={addingQuickTask}
                      placeholder="Add a task… type @ to assign, Enter to add"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleQuickAddTask}
                    disabled={addingQuickTask || !quickTaskText.trim()}
                    style={{
                      ...actionBarBtnBase,
                      padding: '0 12px',
                      fontSize: '0.75rem',
                      border: 'none',
                      background: '#EAF6FD',
                      color: '#2C63AE',
                      cursor: addingQuickTask || !quickTaskText.trim() ? 'default' : 'pointer',
                      opacity: addingQuickTask || !quickTaskText.trim() ? 0.5 : 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {addingQuickTask ? 'Adding…' : '+ Add'}
                  </button>
                </div>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 4 }}>
                  {addingQuickTask
                    ? 'Adding task…'
                    : 'Enter or + Add · @ to assign (optional) · tick the box to mark done'}
                </div>
              </div>
            )}
            {loadingStoryTasks && storyTaskCount === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                Loading tasks…
              </div>
            ) : storyTaskCount === 0 ? (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderRadius: 8,
                  border: '1.5px dashed #cbd5e1',
                  fontSize: '0.85rem',
                  color: '#64748b',
                }}
              >
                No tasks under this story yet.
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {storyChildTasks.map((child) => {
                  const statusMeta = TASK_STATUS_COLORS[child.status] || TASK_STATUS_COLORS.Open;
                  const assignee = child.assignee_name || child.assignee_email || 'Unassigned';
                  const clickable = typeof onOpenChildTask === 'function';
                  const isDone = CLOSED_ISSUE_STATUSES.includes(child.status);
                  const isToggling = togglingTaskIds.includes(child.issueid);
                  return (
                    <div
                      key={child.issueid}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onClick={clickable ? () => onOpenChildTask(child) : undefined}
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onOpenChildTask(child);
                              }
                            }
                          : undefined
                      }
                      style={{
                        // One line by default; wraps to more only when the title needs it.
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 10px',
                        borderRadius: 8,
                        border: '1px solid',
                        borderColor: isDone ? '#bbf7d0' : '#e2e8f0',
                        borderLeft: isDone ? '3px solid #16a34a' : '3px solid #7c3aed',
                        background: isDone ? '#f6fdf9' : '#fff',
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'background 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={
                        clickable
                          ? (e) => {
                              e.currentTarget.style.background = isDone ? '#effbf4' : '#f8fafc';
                            }
                          : undefined
                      }
                      onMouseLeave={
                        clickable
                          ? (e) => {
                              e.currentTarget.style.background = isDone ? '#f6fdf9' : '#fff';
                            }
                          : undefined
                      }
                    >
                      {/* Real checkbox — ticking it closes the task, unticking reopens. */}
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isDone}
                        aria-label={isDone ? 'Mark task as not done' : 'Mark task as done'}
                        disabled={!canEdit || isToggling}
                        onClick={(e) => {
                          stopChildCardNav(e);
                          handleToggleChildTaskDone(child);
                        }}
                        onMouseDown={stopChildCardNav}
                        onPointerDown={stopChildCardNav}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 5,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          border: isDone ? '2px solid #16a34a' : '2px solid #cbd5e1',
                          background: isDone ? '#16a34a' : '#fff',
                          color: '#fff',
                          cursor: canEdit ? (isToggling ? 'wait' : 'pointer') : 'default',
                          opacity: isToggling ? 0.5 : 1,
                          transition: 'background 0.15s, border-color 0.15s',
                          padding: 0,
                          lineHeight: 1,
                        }}
                      >
                        {isDone ? '✓' : ''}
                      </button>
                      {/* Title line — the only thing that grows when text wraps. */}
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: isDone ? '#94a3b8' : '#0f172a',
                          lineHeight: 1.4,
                          overflowWrap: 'anywhere',
                          textDecoration: isDone ? 'line-through' : 'none',
                          textDecorationColor: '#cbd5e1',
                        }}
                        title={taskFirstLine(child)}
                      >
                        <span
                          style={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: isDone ? '#86efac' : '#a78bfa',
                            marginRight: 6,
                            textDecoration: 'none',
                            display: 'inline-block',
                          }}
                        >
                          #{child.issueid}
                        </span>
                        {taskFirstLine(child)}
                        {Number(child.story_points) > 0 && (
                          <span style={{ marginLeft: 6, fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed' }}>
                            {child.story_points}p
                          </span>
                        )}
                      </div>
                      {/* Right rail — assignee + status, side by side on one line. */}
                      <div
                        style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={stopChildCardNav}
                        onMouseDown={stopChildCardNav}
                        onPointerDown={stopChildCardNav}
                      >
                        {canEdit ? (
                          <select
                            value={String(child.assignee_id || '')}
                            disabled={reassigningTaskIds.includes(child.issueid)}
                            onChange={(e) => handleChildTaskReassign(child.issueid, e.target.value)}
                            title={`Assignee: ${assignee}`}
                            style={{
                              width: 122,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '4px 6px',
                              borderRadius: 6,
                              border: '1px solid #e2e8f0',
                              background: '#f8fafc',
                              color: child.assignee_id ? '#334155' : '#94a3b8',
                              cursor: 'pointer',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            <option value="">Unassigned</option>
                            {taskFilteredAssignable.map((u) => (
                              <option key={u.id} value={String(u.id)}>
                                {u.full_name || u.email}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            style={{
                              fontSize: '0.7rem',
                              color: '#64748b',
                              maxWidth: 122,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={assignee}
                          >
                            {assignee}
                          </span>
                        )}
                        {canEdit ? (
                          <select
                            value={isDone ? 'Closed' : child.status || 'Open'}
                            onChange={(e) => handleChildTaskStatusChange(child.issueid, e.target.value)}
                            style={{
                              width: 104,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '4px 6px',
                              borderRadius: 6,
                              border: '1px solid',
                              borderColor: isDone ? '#bbf7d0' : '#e2e8f0',
                              background: isDone ? '#dcfce7' : statusMeta.bg,
                              color: isDone ? '#15803d' : statusMeta.color,
                              cursor: 'pointer',
                            }}
                          >
                            {workflowOptions.map(({ value, label }) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                            <option value="Reopened">↩ {issueStatusLabel('Reopened', boardDepartment)}</option>
                            <option disabled style={{ color: '#d1d5db' }}>──────</option>
                            <option value="Closed">✓ {issueStatusLabel('Done', boardDepartment)}</option>
                          </select>
                        ) : (
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: '0.66rem',
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: 10,
                              background: statusMeta.bg,
                              color: statusMeta.color,
                              whiteSpace: 'nowrap',
                              border: '1px solid rgba(0,0,0,0.06)',
                            }}
                          >
                            {issueStatusLabel(child.status || 'Open', boardDepartment)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/*
          Description.

          The body was always being parsed out of `description` into
          draftDescBody and written back on save, but nothing rendered it — so
          text entered before this modal was rewritten was preserved and
          invisible. This is the missing editor, not a new field.
        */}
        <div style={{ marginBottom: '1rem' }}>
          <div
            onClick={() => setShowDescription(!showDescription)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem',
              background: '#f8fafc',
              borderRadius: 8,
              cursor: 'pointer',
              marginBottom: showDescription ? '0.75rem' : 0,
              border: '1px solid #e2e8f0',
            }}
          >
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#475569', margin: 0 }}>
              Description
              {!showDescription && draftDescBody.trim()
                ? ` — ${draftDescBody.trim().split('\n')[0].slice(0, 60)}${
                    draftDescBody.trim().length > 60 ? '…' : ''
                  }`
                : ''}
            </h3>
            <span style={{ fontSize: '1.2rem', color: '#64748b', fontWeight: 700 }}>
              {showDescription ? '−' : '+'}
            </span>
          </div>

          {/* Description is everyone's to write and correct, unlike the fields
              below it (reassign, status, backlog) which stay steward-only —
              the title field already has no such restriction, and the
              backend accepts a description edit from anyone with project
              access, so this only relaxed what the UI itself was enforcing. */}
          {showDescription && (
            canEditDescription ? (
              /* Grows with the text — a fixed height made a long description
                 scroll inside a small box, hiding most of it. Capped so a very
                 long one cannot push the audit log off the modal. */
              <AutoGrowTextarea
                value={draftDescBody}
                onChange={(e) => setDraftDescBody(e.target.value)}
                minRows={4}
                maxRows={20}
                placeholder="Add more detail — context, links, acceptance criteria…"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                  lineHeight: 1.55,
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              /* whiteSpace preserves the line breaks the text was written with */
              <div
                style={{
                  padding: '0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: '0.85rem',
                  lineHeight: 1.55,
                  color: draftDescBody.trim() ? '#334155' : '#94a3b8',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                {draftDescBody.trim() || 'No description'}
              </div>
            )
          )}
        </div>

        {/* Audit log & comments */}
          <div>
            <div 
              onClick={() => setShowActivities(!showActivities)}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '0.75rem',
                background: '#f8fafc',
                borderRadius: 8,
                cursor: 'pointer',
                marginBottom: showActivities ? '0.75rem' : 0,
                border: '1px solid #e2e8f0',
              }}
            >
              <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#475569', margin: 0 }}>
                Audit log & comments {activitiesData.length > 0 && `(${activitiesData.length})`}
              </h3>
              <span style={{ fontSize: '1.2rem', color: '#64748b', fontWeight: 700 }}>
                {showActivities ? '−' : '+'}
              </span>
            </div>
            
            {showActivities && (
            <>
            {/* Add Comment — top of section so it stays visible without scrolling */}
            <div style={{ marginBottom: '1rem' }}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                style={{
                  width: '100%',
                  minHeight: 80,
                  padding: '0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={handleAddComment}
                disabled={!comment.trim() || addCommentMutation.isPending}
                style={{
                  marginTop: '0.5rem',
                  padding: '8px 16px',
                  background: comment.trim() ? '#1272B3' : '#e2e8f0',
                  color: comment.trim() ? '#fff' : '#94a3b8',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: comment.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {addCommentMutation.isPending ? 'Adding...' : 'Add Comment'}
              </button>
            </div>

            {isLoadingActivities ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                Loading…
              </div>
            ) : activitiesData.length === 0 ? (
              <div style={{
                padding: '1.5rem',
                textAlign: 'center',
                background: '#f8fafc',
                borderRadius: 8,
                color: '#94a3b8',
                fontSize: '0.85rem',
              }}>
                No audit entries or comments yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {activitiesData.map((activity) => {
                  const auditText = formatAuditLine(activity, users);
                  const isComment = activity.action_type === 'commented';
                  const auditNote =
                    !isComment
                    && activity.comment
                    && activity.action_type !== 'created'
                      ? activity.comment
                      : null;
                  return (
                    <div
                      key={activity.id ?? `${activity.created_at}-${activity.action_type}-${activity.issue_id}`}
                      style={{
                        padding: '0.75rem',
                        background: isComment ? '#fff' : '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                            {activity.actor_name || activity.actor_email || 'System'}
                          </span>
                          <span style={{
                            fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                            padding: '2px 6px', borderRadius: 4,
                            background: isComment ? '#e0e7ff' : '#e2e8f0',
                            color: isComment ? '#3730a3' : '#475569',
                          }}>
                            {isComment ? 'Comment' : 'Audit'}
                          </span>
                        </div>
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {new Date(activity.created_at).toLocaleString()}
                        </span>
                      </div>
                      {isComment ? (
                        <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 }}>
                          {activity.comment}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.5 }}>
                          {auditText || (
                            <>
                              {activity.action_type === 'status_changed' && `Status → ${activity.new_value}`}
                              {activity.action_type === 'reassigned' && `Reassigned to ${activity.assignee_name || activity.assignee_email || activity.new_value}`}
                              {activity.action_type === 'assigned' && `Assigned to ${activity.assignee_name || activity.assignee_email || activity.new_value}`}
                              {activity.action_type === 'created' && `Created (${activity.new_value || 'issue'})`}
                              {!['status_changed', 'reassigned', 'assigned', 'created'].includes(activity.action_type) && (activity.action_type || 'Update')}
                            </>
                          )}
                          {auditNote && (
                            <div
                              style={{
                                marginTop: 6,
                                padding: '6px 10px',
                                background: '#fff',
                                borderLeft: '3px solid #cbd5e1',
                                borderRadius: '0 6px 6px 0',
                                whiteSpace: 'pre-wrap',
                                color: '#475569',
                              }}
                            >
                              {auditNote}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </>
            )}
          </div>
      </div>

      {showAddTask && isStory && resolvedProjectId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '1rem',
          }}
          onClick={() => setShowAddTask(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: '100%',
              maxWidth: 580,
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
              padding: '1.25rem 1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontWeight: 800 }}>Add Task to Story</h3>
              <button
                type="button"
                onClick={() => setShowAddTask(false)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>
            <IssueForm
              sprintId={resolvedSprintId}
              projectId={resolvedProjectId}
              parentId={task.issueid}
              fixedType="Task"
              typeOptions={['Task']}
              sprintList={[]}
              taskParentStory={taskParentStory}
              onSuccess={() => {
                setShowAddTask(false);
                qc.invalidateQueries({ queryKey: ['board'] });
                qc.invalidateQueries({ queryKey: ['story-subtasks', task.issueid] });
                qc.invalidateQueries({ queryKey: ['task-activities', task.issueid] });
                onTaskCreated?.();
                toast.success('Task added!');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalTree, document.body);
};

export default TaskDetailModal;
